/*
 * This file is part of glowingblue/orell-thalia-common.
 *
 * Copyright (c) 2023 Glowing Blue AG.
 * Authors: Rafael Horvat.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import app from 'flarum/forum/app';
import { slug } from '../common';
import Model from 'flarum/common/Model';
import User from 'flarum/common/models/User';
import RedirectHome from './components/RedirectHome';
import LoadingPage from './components/LoadingPage';
import addFooter from './extend/addFooter';
import addHomeTour from './extend/addHomeTour';
import addNoAnswerFilter from './extend/addNoAnswerFilter';
import addOFLogin from './extend/addOFLogin';
import allowHtmlInWelcomeHeroTitle from './extend/allowHtmlInWelcomeHeroTitle';
import fixUserPageHistory from './extend/fixUserPageHistory';
import addSignUpAlert from './extend/addSignUpAlert';
import loginExtender from './extend/loginExtender';
import customizeDiscussionHero from './extend/customizeDiscussionHero';
import customizeDiscussionList from './extend/customizeDiscussionList';
import customizeFollowingPage from './extend/customizeFollowingPage';
import customizePlaceholder from './extend/customizePlaceholder';
import customizeSearchSources from './extend/customizeSearchSources';
import customizeTagDiscussionModal from './extend/customizeTagDiscussionModal';
import customizeUserDirectoryPage from './extend/customizeUserDirectoryPage';
import customizeUserPage from './extend/customizeUserPage';
import moveStartDiscussionButton from './extend/moveStartDiscussionButton';
import registrationGtagEvent from './extend/registrationGtagEvent';
import setupNavLinks from './extend/setupNavLinks';
import addIntroTextToHero from './extend/addIntroTextToHero';
import changeIndexPageTitle from './extend/changeIndexPageTitle';
import addTagToCurrentApp from './extend/addTagToCurrentApp';
import insertAdsIntoIndexPage from './extend/insertAdsIntoIndexPage';
import showCountryRedirectPopup from './extend/showCountryRedirectPopup';

export { default as extend } from './extend';
export { components } from './components';
export { states } from './states';
export { helpers } from '../common/helpers';
export { utils } from './utils';

app.initializers.add(
	slug + '-early',
	() => {
		loginExtender();
		addSignUpAlert(); // !! Needs to be called after loginExtender() !!
	},
	1
);

app.initializers.add(
	slug,
	() => {
		// Tags page isn't accessible: we show the home page instead
		app.routes.tags = { path: '/tags', component: RedirectHome };

		// Add User attributes
		User.prototype.canFilterNoAnswer = Model.attribute<boolean>('canFilterNoAnswer');

		User.prototype.linkedShop = function (this: User, allowRefresh: boolean = true): string | boolean | null {
			const value = this.attribute<string | boolean | null>('linkedShop');
			if (allowRefresh && app.store.all('linked-accounts').some((a) => a.id() === `${app.session.user!.id()}-oft`)) {
				if (value && !this._refreshingLinkedShop) {
					this._refreshingLinkedShop = true;
					// Fix the fact that the user object has not been refreshed
					app.store.find('users', app.session.user!.id()!).then(() => {
						this._refreshingLinkedShop = false;
						m.redraw();
					});
				}

				return false;
			}
			return value;
		};

		// Used by rerouteTo() to remount the application content
		app.routes.__loading = { path: '/__loading', component: LoadingPage };

		addFooter();
		addHomeTour();
		addNoAnswerFilter();
		allowHtmlInWelcomeHeroTitle();
		fixUserPageHistory();

		if ('fof-oauth' in flarum.extensions) {
			addOFLogin();
		}

		customizeDiscussionHero();
		customizeDiscussionList();
		customizeFollowingPage();
		customizePlaceholder();
		customizeSearchSources();
		customizeTagDiscussionModal();
		customizeUserDirectoryPage();
		customizeUserPage();
		moveStartDiscussionButton();
		registrationGtagEvent();
		setupNavLinks();
		insertAdsIntoIndexPage();
		showCountryRedirectPopup();

		if (app.initializers.has('glowingblue-newvigation')) {
			addIntroTextToHero();
			changeIndexPageTitle();
		}

		if (app.initializers.has('flarum-tags')) {
			addTagToCurrentApp();
		}
	},
	-1
);
